<template>
  <div class="tabulator-wrapper" ref="wrapper" style="flex: 1 1 0; min-height: 0; overflow: hidden;">
    <div class="alert alert-info new-data" role="alert" v-if="newData" v-on:click="forceReload">
      {{ _('New items available. Click here to reload.') }}
    </div>
    <div class="alert alert-warning new-data" role="alert" v-if="batchProcessing">
      {{ _('Processing, please wait...') }}
    </div>
    <div :class="'spinner' + (dataLoading ? ' show' : '')">
      <div class="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
    </div>
    <div class="tabulator" ref="table"></div>
  </div>
</template>

<script>

import { loadTabulator } from '~/lib/tabulator'
import axios from 'axios'
import debounce from 'lodash/debounce'
import errors from '~/lib/errors'
import { mapGetters } from 'vuex'

export default {
  data () {
    return {
      tabulator: null,
      parentNode: null,
      observer: null,
      sizingOffset: 0,
      numColumns: 0,
      newData: false,
      batchProcessing: false,
      dataLoading: false,
      isInModal: false,
      selectedData: [],
      resizeTimeout: null,
      visibilityObserver: null,
      tabPane: null
    }
  },
  mixins: [errors],
  props: ['url', 'mode', 'openGroups', 'sort', 'options', 'search', 'idFilter', 'typeFilter', 'displayedTypeFilter', 'adminTagFilter', 'tagFilter', 'posFilter', 'categoryFilter', 'poolFilter', 'groupFilter', 'collectionFilter', 'userlevelFilter', 'dateRangeFilter', 'orderDateFilter', 'startDateFilter', 'endDateFilter', 'invoiceDateFilter', 'statusFilter', 'locale', 'dateFormat', 'timeFormat', 'numberFormat'],
  computed: {
    ...mapGetters(['apiKey', 'shopId', 'baseDomain']),
  },
  methods: {
    load (url, config, params) {
      return new Promise (async (resolve, reject) => {
        try {
          this.dataLoading = true
          if (params.sort) {
            params.sorters = []
            params.sort.forEach(sorter => {
              params.sorters.push(JSON.stringify(sorter))
            })
            delete params.sort
          }
          if (params.filter) {
            params.filters = []
            params.filter.forEach(filter => {
              params.filters.push(JSON.stringify(filter))
            })
            delete params.filter
          }
          let response = await axios.get(url, { params: params })
          try {
            if (this.tabulator && !this.tabulator.destroyed) {
              this.selectedData = this.tabulator.getSelectedData()
            }
          } catch (e) {
            this.selectedData = []
          }
          resolve(this.normalizeTabulatorPayload(this.applyDummyChildren(response.data)))
        } catch (e) {
          reject(e)
        } finally {
          this.dataLoading = false
        }
      })
    },
    canAutoRefresh () {
      const tableHolder = this.tabulator?.rowManager?.element
      if (!tableHolder) {
        return true
      }
      const atTop = tableHolder.scrollTop === 0
      const allVisible = tableHolder.scrollHeight <= tableHolder.clientHeight
      return atTop || allVisible
    },
    replaceDataIfSafe () {
      if (!this.tabulator || this.tabulator.destroyed) {
        return
      }
      if (this.canAutoRefresh()) {
        this.tabulator.replaceData()
        this.newData = false
      } else {
        this.newData = true
      }
    },
    reload (newUrl, message = {}) {
      if (newUrl) {
        this.tabulator.setData(newUrl)
      } else {
        if (message && message.data && !message.data.isNew) {
          let path
          let params = [`apikey=${this.apiKey}`];
          switch (message.type) {
            case "categoryUpdate":
              path = 'categories'
              break
            case "productUpdate":
              path = 'products'
              params.push('channels=true')
              break
            case "collectionUpdate":
              path = 'collections'
              break
            case "usergroupUpdate":
              path = 'usergroups'
              break
            case "orderUpdate":
              path = 'orders'
              break
            case "resourceUpdate":
              path = 'resources'
              params.push('capacity=true')
              break
            case "resourcepoolUpdate":
              path = 'resourcepools'
              params.push('resources=true')
              break
          }
          if (message.shopId !== this.shopId) { // We can't have any params if we're loading products from remote shops
            params = []
          }
          params.push('rawformat=true')
          if (path) {
            let loadUrl = `${this.baseDomain}${message.shopId}/${path}/${message.data.id}.json?${params.join('&')}`
            return this.handleUpdate(loadUrl, message.shopId, message.data.id, message.type)
          } else {
            this.replaceDataIfSafe()
          }
        } else {
          this.replaceDataIfSafe()
        }
      }
    },
    setNewData () {
      this.newData = true
    },
    forceReload () {
      this.tabulator.replaceData()
      this.newData = false
    },
    handleUpdate (url, shopId, id, type) {
      axios.get(url)
        .then(response => {
          if (response.data.totalWithDelivery) {
            response.data.totalWithDelivery = parseFloat(response.data.totalWithDelivery)
          }
          if (response.data.price) {
            response.data.price = parseFloat(response.data.price)
          }
          if (response.data.count) {
            response.data.capacity = response.data.count
          }
          switch(type) { // This hack-special-case-only-for-you is required so that clicking reloaded items (mostly products) works
            case 'productUpdate':
              response.data.itemtype = 'product'
              break;
            case 'categoryUpdate':
              response.data.itemtype = 'category'
              break;
          }
          return this.editRow(id, shopId, response.data, type, 'update')
        })
        .catch(error => {
          if (error.response && error.response.status === 404) {
            return this.editRow(id, shopId, {}, type, 'delete')
          }
        })
    },
    editRow (id, shopId, data, type, action) {
      let isUpdated = false
      let forceReload = false
      let items = this.tabulator.searchRows('id', '=', id)
      // Fetch categories we must update with this product update
      let updateCategories = []
      if (type === 'productUpdate' && data.enable_category && shopId === this.shopId) {
        let itemCategories = data.enable_category.split(';')
        data.localSaleschannelCategories.forEach(category => {
          if (itemCategories.includes(category.id)) {
            updateCategories.push(category)
          }
        })
      }
      if (items && items.length > 0) {
        items.forEach(item => {
          let itemData = item.getData()
          switch (type) {
            case 'productUpdate':
            case 'categoryUpdate':
              if (itemData.itemtype !== type.replace('Update', '') && !itemData.shopid) {
                return false
              }
              break
            case 'resourcepoolUpdate':
            case 'resourceUpdate':
              if (data.hasOwnProperty('count') && itemData.displayedtype !== 'buyable') {
                return false
              }
              break
          }
          if (action === 'update') {
            if (itemData.archived !== data.archived) {
              forceReload = true
            }
            this.safeUpdateRow(item, data)
          } else {
            item.delete()
          }
          isUpdated = true
        })
      }

      if (!isUpdated) {
        // No item found which could mean that the item is part of a tree
        let categories = this.tabulator.getData().filter(d => d.itemtype === 'category' || d.itemtype === 'resourcepool')
        categories.forEach(category => {
          let row = this.resolveRow(category.id)
          if (!row || typeof row.getTreeChildren !== 'function') {
            return
          }
          let children = row.getTreeChildren()
          children.forEach(child => {
            let cRow = this.resolveRow(child)
            if (!cRow) {
              return
            }
            let cData = cRow.getData()
            if (cData.id === id && ((shopId === this.shopId && !cData.shopid) || (cData.shopid === shopId))) {
              if (action === 'update') {
                // If this is a product update and product no longer exists in the category, delete it
                // Unless it's a remote product, then we don't know, so let's just update the data and hope for the best
                if (type === 'productUpdate' && !updateCategories.map(c => c.source_id).includes(category.id) && !cData.shopid) {
                  cRow.delete()
                } else {
                  this.safeUpdateRow(cRow, data)
                }
              } else {
                cRow.delete()
              }
              isUpdated = true
            }
          })
        })
      }

      // Add product to categories
      if (type === 'productUpdate' && updateCategories.length > 0) {
        let targetCategoryIds = updateCategories.map(c => c.source_id)
        let targetCategories = this.tabulator.getData().filter(d => d.itemtype === 'category' && targetCategoryIds.includes(d.id))
        targetCategories.forEach(category => {
          let row = this.resolveRow(category.id)
          if (!row || typeof row.getTreeChildren !== 'function') {
            return
          }
          let children = row.getTreeChildren()
          let found = false
          children.forEach(child => {
            let cRow = this.resolveRow(child)
            if (!cRow) {
              return
            }
            let cData = cRow.getData()
            if (cData.id === data.id) {
              found = true
            }
          })
          if (!found) {
            row.addTreeChild(data)
          }
        })
      }

      // Add or remove resources and subresourcepools if either have changed
      if (type === 'resourcepoolUpdate') {
        let resourcepool = this.tabulator.getData().filter(d => d.id === data.id && d.itemtype === 'resourcepool')
        if (resourcepool && resourcepool[0]) {
          let row = this.resolveRow(resourcepool[0].id)
          if (row && typeof row.getTreeChildren === 'function') {
            let children = row.getTreeChildren()
            let childResourceIds = []
            let childResourcepoolIds = []
            children.forEach(child => {
              let cRow = this.resolveRow(child)
              if (!cRow) {
                return
              }
              let cData = cRow.getData()
              if (cData.displayedtype === 'resourcepool') {
                childResourcepoolIds.push(cData.id)
              } else {
                childResourceIds.push(cData.id)
              }
              if (cData.displayedtype !== 'resourcepool' && !data.sharedResource.map(res => res.id).includes(cData.id)) {
                cRow.delete()
              } else if (cData.displayedtype === 'resourcepool' && !data.sharedResourcepool.map(rp => rp.id).includes(cData.id)) {
                cRow.delete()
              }
            })
            data.sharedResource.forEach(res => {
              if (!childResourceIds.includes(res.id)) {
                row.addTreeChild(res)
              }
            })
            data.sharedResourcepool.forEach(rp => {
              if (!childResourcepoolIds.includes(rp.id)) {
                rp.displayedtype = 'resourcepool'
                rp.itemtype = 'subresourcepool'
                rp['_children'] = [{
                  name: this._('Loading...')
                }]
                row.addTreeChild(rp, true) // True adds to top. It's not perfect, but the best I can be arsed to do
              }
            })
            // @todo one more thing you COULD do is re-add any deleted sub-resourcepools to the top level IF they have become level 0
          }
        }
      }

      // If isUpdated still remains false or forceReload is true, reload the table
      if (!isUpdated || forceReload) {
        this.replaceDataIfSafe()
      }
    },
    resolveRow (rowOrId) {
      if (!rowOrId || !this.tabulator) {
        return null
      }
      if (typeof rowOrId.getData === 'function') {
        return rowOrId
      }
      try {
        return this.tabulator.getRow(rowOrId) || null
      } catch (e) {
        return null
      }
    },
    // Do not pass _children into Tabulator — that triggers DataTree.layoutRow
    // on a half-rendered tree child and crashes on cell.getElement().
    rowDataForUpdate (data) {
      if (!data || typeof data !== 'object') {
        return data
      }
      const safe = { ...data }
      delete safe._children
      return safe
    },
    applyRowData (row, data) {
      try {
        const current = row.getData()
        const safe = this.rowDataForUpdate(data)
        if (current && safe) {
          Object.assign(current, safe)
        }
      } catch (e) {}
    },
    // Avoid row.update(): it rewrites the name cell and strips the tree branch.
    // Write data, then reformat so cells and tree chrome are built together.
    // Off-screen rows skip the DOM rebuild (offsetParent is null) but are
    // marked uninitialized so the virtual renderer uses the new data on scroll-in.
    safeUpdateRow (row, data) {
      const target = this.resolveRow(row)
      if (!target) {
        return false
      }
      this.applyRowData(target, data)
      if (typeof target.reformat !== 'function') {
        return true
      }
      try {
        target.reformat()
        return true
      } catch (e) {
        return false
      }
    },
    localizedTimestamp (date) {
      switch (this.locale) {
        case 'fi_FI':
        case 'en_US':
          return date.format(this.dateFormat) + ' ' + this._('at') + ' ' + date.format(this.timeFormat)
        case 'sv_SE':
        case 'it_IT':
        default:
          return date.format(this.dateFormat) + ' ' + date.format(this.timeFormat)
      }
    },
    getAvailableHeight() {
      if (!this.parentNode || !this.$refs.wrapper) {
        return null
      }
      const parent = this.parentNode
      const wrapper = this.$refs.wrapper
      // Use the wrapper's top edge only — not its height — so Tabulator size cannot
      // feed back into the next measurement (that loop made PricingList grow forever).
      const parentRect = parent.getBoundingClientRect()
      const wrapperRect = wrapper.getBoundingClientRect()
      const topOffset = wrapperRect.top - parentRect.top + parent.scrollTop
      const paddingBottom = parseFloat(getComputedStyle(parent).paddingBottom) || 0
      return parent.clientHeight - topOffset - paddingBottom - this.sizingOffset
    },
    tableIsReady () {
      return !!(this.tabulator && !this.tabulator.destroyed && this.tabulator.columnManager && this.tabulator.columnManager.getElement())
    },
    resize() {
      clearTimeout(this.resizeTimeout)
      if (!this.tableIsReady()) {
        return
      }
      if (this.options && this.options.height) {
        const fixedHeight = parseInt(this.options.height, 10)
        if (fixedHeight > 0 && this._lastSetHeight !== fixedHeight) {
          this._lastSetHeight = fixedHeight
          try {
            this.tabulator.setHeight(fixedHeight + 'px')
          } catch (e) {}
        }
        return
      }
      if(this.isInModal) {
        return
      }
      if (!this.parentNode || !this.$refs.wrapper || !this.$refs.table) {
        return
      }

      const height = this.getAvailableHeight()
      if (height === null || height < 200) {
        return
      }
      if (this._lastSetHeight === height) {
        return
      }
      this._lastSetHeight = height

      try {
        this.tabulator.setHeight(height + 'px')
      } catch (e) {}
      if (!this.tableIsReady() || !this.tabulator.rowManager || !this.tabulator.rowManager.rows) {
        return
      }
      this.tabulator.rowManager.rows.forEach((row) => {
        if (!row || !row.element) return
        try {
          row.calcHeight()
        } catch (e) {}
      })
    },
    removePreviousFilter(field) {
      let previousFilters = this.tabulator.getFilters().filter(f => f.field === field)
      if (previousFilters && previousFilters.length > 0) {
        this.tabulator.removeFilter(previousFilters)
      }
    },
    freeTextSearch: debounce(function(s) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'search')
      if (s) {
        filters.push({ field: 'search', type: 'like', value: s })
      } else {
        return this.removePreviousFilter('search')
      }
      this.tabulator.setFilter(filters)
    }, 200),
    filterByType(type) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'type')
      if (type && type !== 'all') {
        filters.push({ field: 'type', type: '=', value: type })
      } else {
        return this.removePreviousFilter('type')
      } 
      this.tabulator.setFilter(filters)
    },
    filterById(id) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'id')
      if (id && id !== 'all') {
        filters.push({ field: 'id', type: '=', value: id })
      } else {
        return this.removePreviousFilter('id')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByDisplayedType(type) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'displayedtype')
      if (type && type !== 'all') {
        filters.push({ field: 'displayedtype', type: '=', value: type })
      } else {
        return this.removePreviousFilter('displayedtype')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByStatus(status) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'status')
      if (status && status !== 'all') {
        filters.push({ field: 'status', type: '=', value: status })
      } else {
        return this.removePreviousFilter('status')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByDateRange(range) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'ts')
      if (range) {
        filters.push({ field: 'ts', type: '=', value: range })
      } else {
        return this.removePreviousFilter('ts')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByStartDate(range) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'start_ts')
      if (range) {
        filters.push({ field: 'start_ts', type: '=', value: range })
      } else {
        return this.removePreviousFilter('start_ts')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByEndDate(range) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'end_ts')
      if (range) {
        filters.push({ field: 'end_ts', type: '=', value: range })
      } else {
        return this.removePreviousFilter('end_ts')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByOrderDate(range) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'orderdate')
      if (range) {
        filters.push({ field: 'orderdate', type: '=', value: range })
      } else {
        return this.removePreviousFilter('orderdate')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByInvoiceDate(range) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'invoicedate')
      if (range) {
        filters.push({ field: 'invoicedate', type: '=', value: range })
      } else {
        return this.removePreviousFilter('invoicedate')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByTag(tag) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'tag')
      if (tag) {
        filters.push({ field: 'tag', type: '=', value: tag })
      } else {
        return this.removePreviousFilter('tag')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByAdminTag(tag) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'admintag')
      if (tag) {
        filters.push({ field: 'admintag', type: '=', value: tag })
      } else {
        return this.removePreviousFilter('admintag')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByPos(pos) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'pos_name')
      if (pos) {
        filters.push({ field: 'pos_name', type: '=', value: pos })
      } else {
        return this.removePreviousFilter('pos_name')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByPool(pool) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'resourcepool')
      if (pool) {
        filters.push({ field: 'resourcepool', type: '=', value: pool })
      } else {
        return this.removePreviousFilter('resourcepool')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByCategory(category) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'category')
      if (category) {
        filters.push({ field: 'category', type: '=', value: category })
      } else {
        return this.removePreviousFilter('category')
      } 
      this.tabulator.setFilter(filters)
    },
    filterByGroup(group) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'group')
      if (group && group !== 'all') {
        filters.push({ field: 'group', type: '=', value: group })
      } else {
        return this.removePreviousFilter('group')
      }
      this.tabulator.setFilter(filters)
    },
    filterByCollection(collection) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'collection')
      if (collection && collection !== 'all') {
        filters.push({ field: 'collection', type: '=', value: collection })
      } else {
        return this.removePreviousFilter('collection')
      }
      this.tabulator.setFilter(filters)
    },
    filterByUserlevel(level) {
      let filters = this.tabulator.getFilters().filter(f => f.field !== 'userlevel')
      if (level && level !== 'all') {
        filters.push({ field: 'userlevel', type: '=', value: level })
      } else {
        return this.removePreviousFilter('userlevel')
      }
      this.tabulator.setFilter(filters)
    },
    normalizeTabulatorPayload (payload) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return payload
      }
      const lastPage = payload.last_page != null ? parseInt(payload.last_page, 10) || 1 : payload.last_page
      if (payload.data && !Array.isArray(payload.data)) {
        return { ...payload, last_page: lastPage, data: Object.values(payload.data) }
      }
      if (payload.data && lastPage !== payload.last_page) {
        return { ...payload, last_page: lastPage }
      }
      return payload
    },
    applyDummyChildren(result, parentType) {
      if (!parentType) {
        parentType = this.options.parentType
      }
      if (!this.options.dataTree || !parentType) {
        return result
      }
      for (let i = 0; i < result.data.length; i++) {
        if (result.data[i].itemtype === parentType) {
          result.data[i]['_children'] = [{
            name: this._('Loading...')
          }]
        }
      }
      return result
    },
    async expandRow(row, level) {
      if (row && row._row && row._row.data && row._row.data.id) {
        let response
        let params = {}
        const sorters = this.tabulator.getSorters()
        if (sorters && sorters.length) {
          const s = sorters[0]
          params['sorters[]'] = {
            field: s.field,
            dir: s.dir
          }
        }
        params['filters[]'] = {
          field: this.options.parentField,
          type: '=',
          value: row._row.data.id
        }
        // These parameters are needed to get the backend to load the data in Tabulator format (with formatters, etc)
        params['page'] = 1
        params['size'] = 100000
        try {
          response = await axios.get(this.url, { params: params })
        } catch(e) {
          return
        }
        if (this.options.parentType) {
          this.applyDummyChildren(response.data, 'sub' + this.options.parentType)
        }
        // Why do we have this exception here for sub-levels? Somehow doing this to the main level causes an error when automatically reopening previously opened categories
        if (level > 0) {
          row._row.data._children = []
          for (let item of response.data.data.filter(d => d.itemtype !== row._row.data.itemtype)) {
            row.addTreeChild(item)
          }
          // If we don't really have any children, just close the item (instead of showing the "Loading..."" dummy child)
          if (!row._row.data._children.length) {
            setTimeout(() => {
              row.treeCollapse()
            }, 200)
          }
        } else {
          row._row.data._children = response.data.data.filter(d => d.itemtype !== row._row.data.itemtype) // Filter our the parent since it's already visible
          this.tabulator.updateData([row._row.data])
        }
        if (this.options.dataTreeRowExpanded) {
          
          
          
          
          // This causes infinite recursion in the current version of tabulator or vue
          this.options.dataTreeRowExpanded(row, level)


        }
      }
    },
    makePersistenceId (url) {
      return url.replace(/(https:..|.apikey=.*)/g, '').replace(/[^a-z0-9]+/ig, '-').replace(/-[0-9]+-/, '')
    },
    getFilter(field) {
      return this.tabulator.getFilters().find(f => f.field === field)
    },
    initObserver() {
      let $this = this
      // create the observer
      const observer = new ResizeObserver(function (entries) {
        $this.resize()
      })
      this.observer = observer
      if (this.parentNode) {
        // Parent viewport size changes (column layout); do not key off wrapper
        // height — that follows Tabulator and caused unbounded growth in tabbed views.
        observer.observe(this.parentNode)
      }
      if (this.$refs.wrapper) {
        // When filters above the table expand/collapse in a flex column, the
        // wrapper's allocated size changes while parent size stays the same.
        observer.observe(this.$refs.wrapper)
      }
      if (this.$refs.table && this.$refs.table.closest('.tab-content') && this.$refs.table.closest('.tab-content').previousElementSibling) {
        // observe element's specified mutations
        observer.observe(this.$refs.table.closest('.tab-content').previousElementSibling)
        // add the observer to data so we can disconnect it later
      }
      if (this.parentNode && this.parentNode.querySelector('.list-filter-row') && this.parentNode.querySelector('.list-filter-row').previousElementSibling) {
        observer.observe(this.parentNode.querySelector('.list-filter-row').previousElementSibling)
        // add the observer to data so we can disconnect it later
      }
      // Listen for transition end events on the wrapper's parent tab pane
      const tabPane = this.$refs.wrapper.closest('.tab-pane')
      if (tabPane) {
        const transitionEndHandler = () => {
          $this.resize()
        }
        tabPane.addEventListener('transitionend', transitionEndHandler)
        // Store reference for cleanup
        this.transitionEndHandler = transitionEndHandler
        this.tabPane = tabPane
      }      
    }     
  },
  watch: {
    'options.selectable': {
      handler (newValue) {
        if (!newValue) {
          this.tabulator.deselectRow()
          this.$emit('selection', this.tabulator.getSelectedData())
        }
      }
    },
    options: {
      deep: true,
      handler (newValue, oldValue) {
        if (!this.tabulator || this.tabulator.destroyed) {
          return
        }
        if (newValue.columns.length !== this.numColumns) {
          this.tabulator.setColumns(newValue.columns)
          this.numColumns = newValue.columns.length
        }
      }
    },
    url (newUrl, oldUrl) {
      if (!this.tabulator || this.tabulator.destroyed) {
        return
      }
      if (newUrl && newUrl !== oldUrl) {
        this.reload(newUrl)
      }
    },
    search (newValue) {
      const f = this.getFilter('search')
      if (f && f.value === newValue) {
        return
      }
      this.freeTextSearch(newValue)
    },
    idFilter (newValue) {
      const f = this.getFilter('id')
      if (f && f.value === newValue) {
        return
      }
      this.filterById(newValue)
    },
    typeFilter (newValue) {
      const f = this.getFilter('type')
      if (f && f.value === newValue) {
        return
      }
      this.filterByType(newValue)
    },
    displayedTypeFilter (newValue) {
      const f = this.getFilter('displayedtype')
      if (f && f.value === newValue) {
        return
      }
      this.filterByDisplayedType(newValue)
    },
    statusFilter (newValue) {
      const f = this.getFilter('status')
      if (f && f.value === newValue) {
        return
      }
      this.filterByStatus(newValue)
    },
    dateRangeFilter (newValue) {
      const f = this.getFilter('ts')
      if (f && f.value === newValue) {
        return
      }
      this.filterByDateRange(newValue)
    },
    startDateFilter (newValue) {
      const f = this.getFilter('start_ts')
      if (f && f.value === newValue) {
        return
      }
      this.filterByStartDate(newValue)
    },
    endDateFilter (newValue) {
      const f = this.getFilter('end_ts')
      if (f && f.value === newValue) {
        return
      }
      this.filterByEndDate(newValue)
    },
    orderDateFilter (newValue) {
      const f = this.getFilter('orderdate')
      if (f && f.value === newValue) {
        return
      }
      this.filterByOrderDate(newValue)
    },
    invoiceDateFilter (newValue) {
      const f = this.getFilter('invoicedate')
      if (f && f.value === newValue) {
        return
      }
      this.filterByInvoiceDate(newValue)
    },
    tagFilter (newValue) {
      const f = this.getFilter('tag')
      if (f && f.value === newValue) {
        return
      }
      this.filterByTag(newValue)
    },
    adminTagFilter (newValue) {
      const f = this.getFilter('admintag')
      if (f && f.value === newValue) {
        return
      }
      this.filterByAdminTag(newValue)
    },
    posFilter (newValue) {
      const f = this.getFilter('pos_name')
      if (f && f.value === newValue) {
        return
      }
      this.filterByPos(newValue)
    },
    poolFilter (newValue) {
      const f = this.getFilter('resourcepool')
      if (f && f.value === newValue) {
        return
      }
      this.filterByPool(newValue)
    },
    categoryFilter (newValue) {
      const f = this.getFilter('category')
      if (f && f.value === newValue) {
        return
      }
      this.filterByCategory(newValue)
    },
    groupFilter (newValue) {
      const f = this.getFilter('group')
      if (f && f.value === newValue) {
        return
      }
      this.filterByGroup(newValue)
    },
    collectionFilter (newValue) {
      const f = this.getFilter('collection')
      if (f && f.value === newValue) {
        return
      }
      this.filterByCollection(newValue)
    },
    userlevelFilter (newValue) {
      const f = this.getFilter('userlevel')
      if (f && f.value === newValue) {
        return
      }
      this.filterByUserlevel(newValue)
    }
  },
  async mounted () {
    const $this = this
    let sort = []
    let height = null

    if (this.sort && typeof this.sort !== 'undefined') {
      sort = this.sort
    } else if (this.options.initialSort) {
      sort = JSON.parse(JSON.stringify(this.options.initialSort))
    }
    const knownFields = new Set((this.options.columns || []).map(col => col && col.field).filter(Boolean))
    sort = (Array.isArray(sort) ? sort : []).filter(item => item && knownFields.has(item.column))
    if (this.$refs.table.closest('.tabulator-parent')) {
      this.parentNode = this.$refs.table.closest('.tabulator-parent')
    } else {
      this.parentNode = this.$refs.table.parentNode.parentNode
    }
    if (this.$refs.table.closest('.modal-content')) { // Open tables in modals with a fixed height
      this.isInModal = true
    }
    if (this.$refs.table.closest('.tab-pane') || this.$refs.table.closest('.dashboard')) {
      this.sizingOffset = 50 // Tables inside tabs need more hacky offsets
    } else {
      this.sizingOffset = 15
    }

    if (this.options.height) {
      height = parseInt(this.options.height, 10)
    } else if(this.isInModal) {
      height = 300
    } else {
      height = this.getAvailableHeight()
      if (height === null || height < 200 || Number.isNaN(height)) {
        height = 300
      }
    }

    this.initObserver()
    let options
    if (this.mode === 'local') {
      options = {
        layout: 'fitDataFill',
        // layout: 'fitColumns',
        layoutColumnsOnNewData: true,
        ajaxURL: this.url,
        ajaxRequestFunc: this.load,
        sortMode: 'remote',
        filterMode: 'remote',
        columns: this.options.columns,
        height: height + "px",
        persistenceMode: 'local',
        initialSort: sort,
        persistence: this.options.persistence === false ? false : {
          sort: true,
          filter: this.options.persistFilters !== false,
          columns: false
        },
        selectable: true,
        dataLoader: false
      }       
    } else {
      options = {
        layout: this.options.layout || 'fitDataFill',
        // layout: 'fitColumns',
        layoutColumnsOnNewData: true,
        ajaxURL: this.url,
        ajaxRequestFunc: this.load,
        columns: this.options.columns,
        sortMode: 'remote',
        filterMode: 'remote',
        height: height + "px",
        persistenceMode: 'local',
        initialSort: sort,
        persistence: this.options.persistence === false ? false : {
          sort: true,
          filter: this.options.persistFilters !== false,
          columns: false
        },
        //persistentLayout: true,
        //persistentSort: true,
        //persistentFilter: true,
        selectable: true,
        dataLoader: false
      }
      if (this.options.progressiveLoad !== false) {
        options.progressiveLoad = this.options.progressiveLoad || 'scroll'
        options.progressiveLoadScrollMargin = 300
        options.paginationSize = this.options.paginationSize || 50
      } else if (this.options.paginationSize) {
        options.pagination = true
        options.paginationMode = 'remote'
        options.paginationSize = this.options.paginationSize
      }
    }
    options.selectableCheck = (row) => {
      return false
    }
    if (this.options.persistence !== false) {
      if (this.options.persistenceId) {
        options.persistenceID = this.options.persistenceId
      } else {
        options.persistenceID = this.makePersistenceId(this.url)
      }
      if (this.options.persistenceSuffix) {
        options.persistenceID += '-' + this.options.persistenceSuffix
      }
    }
    // For some fucking reason this following option starts creating "too much recursion" errors! WTF
    /*
    if (this.options.initialSort) {
      options.initialSort = this.options.initialSort
    }
    */
    if (this.options.rowFormatter) {
      options.rowFormatter = this.options.rowFormatter
    }
    if (this.options.responsiveLayoutCollapseFormatter) {
      options.responsiveLayout = 'collapse'
      options.responsiveLayoutCollapseFormatter = this.options.responsiveLayoutCollapseFormatter
    } else {
      options.responsiveLayout = 'hide'
    }
    if (this.options.dataTree) {
      options.dataTree = true
    }
    if (this.options.index) {
      options.index = this.options.index
    }
    let filter = []
    if (this.search) {
      filter.push({ field: 'search', type: 'like', value: this.search })
    }
    if (this.typeFilter && this.typeFilter !== 'all') {
      filter.push({ field: 'type', type: '=', value: this.typeFilter })
    }
    if (this.categoryFilter && this.categoryFilter !== 'all') {
      filter.push({ field: 'category', type: '=', value: this.categoryFilter })
    }
    if (this.idFilter && this.idFilter !== 'all') {
      filter.push({ field: 'id', type: '=', value: this.idFilter })
    }
    if (this.displayedTypeFilter && this.displayedTypeFilter !== 'all') {
      filter.push({ field: 'displayedtype', type: '=', value: this.typeFilter })
    }
    if (this.statusFilter && this.statusFilter !== 'all') {
      filter.push({ field: 'status', type: '=', value: this.statusFilter })
    }
    if (this.dateRangeFilter) {
      filter.push({ field: 'ts', type: '=', value: this.dateRangeFilter })
    }
    if (this.startDateFilter) {
      filter.push({ field: 'start_ts', type: '=', value: this.startDateFilter })
    }
    if (this.endDateFilter) {
      filter.push({ field: 'end_ts', type: '=', value: this.endDateFilter })
    }
    if (this.orderDateFilter) {
      filter.push({ field: 'orderdate', type: '=', value: this.orderDateFilter })
    }
    if (this.invoiceDateFilter) {
      filter.push({ field: 'invoicedate', type: '=', value: this.invoiceDateFilter })
    }
    if (this.tagFilter) {
      filter.push({ field: 'tag', type: '=', value: this.tagFilter })
    }
    if (this.adminTagFilter) {
      filter.push({ field: 'admintag', type: '=', value: this.adminTagFilter })
    }
    if (this.posFilter) {
      filter.push({ field: 'pos_name', type: '=', value: this.posFilter })
    }
    if (this.poolFilter) {
      filter.push({ field: 'resourcepool', type: '=', value: this.poolFilter })
    }
    if (this.groupFilter && this.groupFilter !== 'all') {
      filter.push({ field: 'group', type: '=', value: this.groupFilter })
    }
    if (this.collectionFilter && this.collectionFilter !== 'all') {
      filter.push({ field: 'collection', type: '=', value: this.collectionFilter })
    }
    if (this.userlevelFilter && this.userlevelFilter !== 'all') {
      filter.push({ field: 'userlevel', type: '=', value: this.userlevelFilter })
    }
    if (filter.length) {
      options.initialFilter = filter
    }
    // Note: this causes quite a bit of extra load, because Tabulator wants to close all the tree nodes when 
    // we load more data by scrolling down... Which means that each open node needs to be reopened, which
    // means that each reopened node needs to be reloaded with ajax. Ugh.
    const Tabulator = await loadTabulator()
    if (!this.$refs.table) {
      return // Unmounted while Tabulator was loading
    }
    this.tabulator = new Tabulator(this.$refs.table, options)
    this.numColumns = options.columns.length
    this.tabulator.on('renderComplete', async () => {
      if (this.tabulator && this.selectedData) {
        this.tabulator.selectRow(this.selectedData.map(data => data.id))
      }
    })
    this.tabulator.on('dataLoading', () => {
      this.dataLoading = true
    })
    this.tabulator.on('dataProcessed', () => {
      this.dataLoading = false
      if (this.tabulator && this.tabulator.rowManager && this.tabulator.rowManager.rows) {
        this.tabulator.rowManager.rows.forEach((row) => {
          if (!row || !row.element) return
          try {
            row.calcHeight()
          } catch (e) {}
        })
      }
      if (this.tabulator && this.openGroups) {
        const rowObjs = this.tabulator.getRows()
        const openIds = this.openGroups.map(g => g.id)
        rowObjs.forEach((row) => {
          const data = row.getData()
          if (openIds.includes(data.id)) {
            row.treeExpand()
          }
        })
      }
    })
    this.tabulator.on('cellEditing', (cell) => {
      this.$emit('editing', cell)
    })
    this.tabulator.on('cellEdited', (cell) => {
      this.$emit('edited', cell)
    })
    this.tabulator.on('cellEditCancelled', (cell) => {
      this.$emit('editcancelled', cell)
    })
    if (this.options.rowClick) {
      this.tabulator.on('rowClick', (e, row) => {
        if (this.options.dataTree && row && row._row && row._row.data && (row._row.data.itemtype === this.options.parentType || row._row.data.itemtype === 'sub' + this.options.parentType)) {
          // Collapsing rebuilds the row's cells, so don't toggle while a cell is being edited:
          // a click inside the editor never toggles, and a click elsewhere waits until the edit's
          // blur and validation have finished, then toggles only if the row is still in the table
          const target = e && e.target
          if (!(target && target.closest && target.closest('.tabulator-editing'))) {
            const tableElement = this.tabulator.element
            if (tableElement && tableElement.querySelector('.tabulator-editing')) {
              setTimeout(() => {
                const rowElement = this.tabulator && row.getElement()
                if (rowElement && rowElement.isConnected) {
                  row.treeToggle()
                }
              }, 0)
            } else {
              row.treeToggle()
            }
          }
        }
        this.options.rowClick(e, row)
        // Uggh this needs some better logic ffs
        // 'transactionid' in row.getData() means that it's probably an order
        if (this.options.selectable && (row.getData().itemtype === 'product' || row.getData().itemtype === 'resource' || ('transactionid' in row.getData()))) {
          if (row.getElement().classList.contains('tabulator-selected')) {
            row.deselect()
          } else {
            row.select()
          }
          this.$emit('selection', this.tabulator.getSelectedData())
        }
      })
    }
    if (this.options.dataTree) {
      this.tabulator.on('dataTreeRowExpanded', this.expandRow)
      if (this.options.dataTreeRowCollapsed) {
        this.tabulator.on('dataTreeRowCollapsed', this.options.dataTreeRowCollapsed)
      }
    }
    this.tabulator.on('dataFiltering', (filters) => {
      this.$emit('data', {
        filters
      })
    })
    nextTick(() => {
      this.resize()
    })
  },
  created() {
    window.addEventListener('resize', this.resize);
    this.EventBus.$on('redrawDataTable', this.resize)
    this.EventBus.$on('batchResourceUpdateBegin', () => {
      this.batchProcessing = true
    })
    this.EventBus.$on('batchResourceUpdateEnd', () => {
      this.batchProcessing = false
    })
  },
  beforeUnmount() {
    clearTimeout(this.resizeTimeout)
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
    if (this.transitionEndHandler && this.tabPane) {
      this.tabPane.removeEventListener('transitionend', this.transitionEndHandler)
    }
    window.removeEventListener('resize', this.resize)
    this.EventBus.$off('redrawDataTable', this.resize)
    this.EventBus.$off('batchResourceUpdateBegin')
    this.EventBus.$off('batchResourceUpdateEnd')
    if (this.tabulator) {
      this.tabulator.destroy()
      this.tabulator = null
    }
  }
}

</script>
